import { useEffect, useRef, type RefObject } from "react";

/* Sub-pixel rounding, so a chip flush with the cut counts as inside it. */
const CLAMP_SLACK = 1;

/**
 * Whether a chip's control is inside the clamp rather than behind it.
 *
 * ⚠ MEASURED LIVE, not read off the render. Expanding the row sets
 * `max-block-size: none`, so every rect then sits inside the list and nothing
 * tests as clipped — one test covering both states, with no flag to keep in
 * step. jsdom reports every rect as zero, where `0 <= 0 + 1` keeps them all.
 */
function withinClamp(control: HTMLElement, list: HTMLElement): boolean {
  return (
    control.getBoundingClientRect().bottom <=
    list.getBoundingClientRect().bottom + CLAMP_SLACK
  );
}

/**
 * AX11 — "after a chip is removed with the keyboard, focus moves to the next
 * chip, or to the search field if none are left".
 *
 * One job: the move, after the render that dropped the chip. Nothing renders
 * from it, which is why the pending index is a ref and not state.
 *
 * ⚠ The effect is keyed on the chip COUNT rather than on the pending index. A
 * ref does not re-render, so keying on it would never fire; the count is what
 * actually changes, and it changes exactly once per removal.
 *
 * ⚠ A chip the collapsed clamp cuts off is NOT a target (SC 2.4.11 — see
 * `chip-row.tsx`), so the move skips it and lands on the last visible one
 * instead.
 *
 * @param listRef     the chip list, whose buttons are the targets
 * @param chipCount   how many chips are rendered now
 * @param focusField  where focus goes when the row has emptied
 */
export function useChipFocus(
  listRef: RefObject<HTMLElement | null>,
  chipCount: number,
  focusField: () => void
): (index: number) => void {
  const pending = useRef<number | null>(null);

  useEffect(() => {
    const index = pending.current;
    if (index === null) {
      return;
    }
    pending.current = null;
    const list = listRef.current;
    const buttons = list ?
      Array.from(list.querySelectorAll<HTMLButtonElement>("button"))
        .filter(button => withinClamp(button, list)) :
      [];
    /* The chip that took the removed one's place, or the last one when the
       removed chip was itself the last. */
    const next = buttons[Math.min(index, buttons.length - 1)];
    if (next) {
      next.focus();
    } else {
      focusField();
    }
    /* ⚠ `focusField` is deliberately NOT a dependency: it is a fresh closure
       every render and is only ever called from inside this effect, so listing
       it would re-run on every render and move focus nobody asked to move. */
  }, [chipCount, listRef]);

  return (index: number) => {
    pending.current = index;
  };
}
